import { Store } from "@reduxjs/toolkit";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";

import {
  logout as logoutAction,
  signIn,
  updateAuthReady,
  updateNotAuthenticated,
} from "../slice";
import {
  AuthConfig,
  KeycloakSetupOptions,
  IAuthProvider,
} from "../interfaces/IAuthProvider";
import {
  KEYCLOAK_SESSION_ACTIVE_KEY,
  KEYCLOAK_TOKEN_KEY,
  KEYCLOAK_REFRESH_TOKEN_KEY,
  KEYCLOAK_SILENT_SSO_ATTEMPT_KEY,
  NETWORK_DISCONNECT_EVENT,
} from "../constants";
import { isSafari } from "react-device-detect";
import { isNetworkDisconnectError } from "../utils/isNetworkDisconnectError";

export async function exitPageFullscreenSafari() {
  if (!isSafari) return;
  try {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (doc.exitFullscreen) return await doc.exitFullscreen();
    if (doc.webkitExitFullscreen) return await doc.webkitExitFullscreen();
  } catch (e) {
    console.error("[exitPageFullscreen] failed:", e);
  }
}

// True when the current URL is a Keycloak OAuth callback return (state + code/error/session_state).
function isKeycloakCallbackUrl(): boolean {
  const params = new URLSearchParams(
    window.location.search + "&" + window.location.hash.replace(/^#/, ""),
  );
  return (
    params.has("state") &&
    (params.has("code") || params.has("error") || params.has("session_state"))
  );
}

export class KeycloakProvider implements IAuthProvider<KeycloakSetupOptions> {
  private config: AuthConfig;
  private keycloakInstance: Keycloak | null = null;
  private store: Store | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private getLocale: () => string | undefined = () => undefined;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  async setup(
    options: KeycloakSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.store = options.store;
    if (options.getLocale) this.getLocale = options.getLocale;
    const dispatch = this.store.dispatch;

    // Reuse existing instance
    if (this.keycloakInstance) {
      if (this.keycloakInstance.authenticated) dispatch(signIn());
      dispatch(updateAuthReady(true));
      return { authenticated: this.keycloakInstance.authenticated ?? false };
    }

    const wasSessionActive =
      localStorage.getItem(KEYCLOAK_SESSION_ACTIVE_KEY) === "true";
    const { silentCheckSsoRedirectUri = "/silent-check-sso.html", onLoad } =
      options;
    const urlOnLoad = new URLSearchParams(window.location.search).get(
      "onLoad",
    ) as "login-required" | "check-sso" | undefined;

    const onLoadStrategy =
      urlOnLoad ||
      onLoad ||
      options.onLoad ||
      (!wasSessionActive ? "login-required" : "check-sso");

    localStorage.setItem(KEYCLOAK_SESSION_ACTIVE_KEY, "true");

    // Restore previously saved tokens to bypass the check-sso iframe.
    // This avoids third-party cookie issues (requestStorageAccess denied)
    // that cause check-sso to fail on reload for certain auth flows (e.g. phone OTP).
    const savedToken = localStorage.getItem(KEYCLOAK_TOKEN_KEY) ?? undefined;
    const savedRefreshToken =
      localStorage.getItem(KEYCLOAK_REFRESH_TOKEN_KEY) ?? undefined;

    // Fresh/manual loads clear the one-shot recovery flag; keep it only while
    // processing a redirect return so an interrupted reload can still recover.
    if (!isKeycloakCallbackUrl()) {
      sessionStorage.removeItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY);
    }

    const keycloakConfig = {
      url: this.config.serverUrl,
      realm: this.config.realm,
      clientId: this.config.clientId,
    };
    this.keycloakInstance = new Keycloak(keycloakConfig);
    this.keycloakInstance.onAuthRefreshSuccess = () => this.saveTokens();

    try {
      if (!window.navigator.onLine) throw new Error("No internet connection");

      const authenticated = await this.initWithTimeout({
        onLoad: onLoadStrategy,
        ...(onLoadStrategy === "check-sso" && {
          silentCheckSsoRedirectUri:
            window.location.origin + silentCheckSsoRedirectUri,
          silentCheckSsoFallback: true,
        }),
        token: savedToken,
        refreshToken: savedRefreshToken,
        checkLoginIframe: false,
        enableLogging: true,
        pkceMethod: "S256",
        locale: this.getLocale(),
      });

      if (authenticated) {
        this.saveTokens();
        sessionStorage.removeItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY);
        dispatch(signIn());
        return { authenticated: true };
      }

      const hadAuthSession = !!savedRefreshToken;
      const triedSilentSso =
        sessionStorage.getItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY) === "true";

      if (hadAuthSession && !triedSilentSso) {
        sessionStorage.setItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY, "true");
        await this.login();
        return { authenticated: false };
      }

      sessionStorage.removeItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY);
      dispatch(updateNotAuthenticated());
      return { authenticated: false };
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.startsWith("Keycloak init timed out")
      ) {
        const triedSilentSso =
          sessionStorage.getItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY) === "true";
        if (savedToken && savedRefreshToken && !triedSilentSso) {
          sessionStorage.setItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY, "true");
          await this.login();
          return { authenticated: false };
        }
        sessionStorage.removeItem(KEYCLOAK_SILENT_SSO_ATTEMPT_KEY);
      }
      if (isNetworkDisconnectError(error)) {
        window.dispatchEvent(new CustomEvent(NETWORK_DISCONNECT_EVENT));
      }
      console.log("Failed to initialize Keycloak", error);
      return { authenticated: false };
    } finally {
      dispatch(updateAuthReady(true));
    }
  }

  private async initWithTimeout(
    initOptions: KeycloakInitOptions,
    timeoutMs = 5000,
  ): Promise<boolean> {
    if (!this.keycloakInstance) return false;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`Keycloak init timed out after ${timeoutMs}ms`)),
        timeoutMs,
      );
    });

    try {
      return await Promise.race([
        this.keycloakInstance.init(initOptions),
        timeout,
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  private saveTokens(): void {
    if (this.keycloakInstance?.token) {
      localStorage.setItem(KEYCLOAK_TOKEN_KEY, this.keycloakInstance.token);
    }
    if (this.keycloakInstance?.refreshToken) {
      localStorage.setItem(
        KEYCLOAK_REFRESH_TOKEN_KEY,
        this.keycloakInstance.refreshToken,
      );
    }
  }

  async login(redirectUri?: string): Promise<void> {
    try {
      await exitPageFullscreenSafari();
      await this.keycloakInstance?.login({
        redirectUri: redirectUri || window.location.href,
        locale: this.getLocale(),
      });
    } catch (error) {
      const errorMessage =
        typeof error === "string" ? error : (error as Error).message;
      console.log("Failed to login with Keycloak", errorMessage);
    }
  }

  async logout(ignoreDispatch = false): Promise<void> {
    try {
      await exitPageFullscreenSafari();
      localStorage.removeItem(KEYCLOAK_SESSION_ACTIVE_KEY);
      localStorage.removeItem(KEYCLOAK_TOKEN_KEY);
      localStorage.removeItem(KEYCLOAK_REFRESH_TOKEN_KEY);
      if (this.keycloakInstance) {
        // get the root url without query params
        const rootUrl = window.location.origin.split("?")[0];
        await this.keycloakInstance.logout({
          redirectUri: rootUrl,
        });
        this.keycloakInstance = null;
        if (!ignoreDispatch && this.store) {
          this.store.dispatch(logoutAction());
        }
      }
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  async getToken(): Promise<string | undefined> {
    try {
      if (this.keycloakInstance) {
        if (!this.refreshPromise) {
          this.refreshPromise = this.keycloakInstance
            .updateToken(30)
            .finally(() => {
              this.refreshPromise = null;
            });
        }
        const refreshed = await this.refreshPromise;
        if (refreshed) this.saveTokens();
        return this.keycloakInstance.token;
      }
    } catch (error) {
      console.log(error);
    }
    return undefined;
  }

  getCurrentToken(): string | undefined {
    return this.keycloakInstance?.token;
  }

  getClientId(): string {
    return this.config.clientId;
  }

  isInternalAccount(): boolean {
    const roles = this.keycloakInstance?.tokenParsed?.realm_access?.roles || [];
    const hasAccess = roles.some((role) => ["QA", "ADMIN"].includes(role));
    return hasAccess;
  }
}
